import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui';
import PluginCard from './PluginCard';
import type { ExploreCandidate } from '../../types';

interface PluginPickerProps {
  candidates: ExploreCandidate[];
  /** Selected `subdir` (the picker matches by subdir since that's the install key). */
  selectedSubdir: string | undefined;
  onSelect: (candidate: ExploreCandidate) => void;
  /** Used only when `candidates` is empty — the user types a subdir directly. */
  manualSubdir: string;
  onManualSubdirChange: (value: string) => void;
}

export default function PluginPicker({
  candidates,
  selectedSubdir,
  onSelect,
  manualSubdir,
  onManualSubdirChange,
}: PluginPickerProps) {
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);
  // Track keyboard focus position separately from selection so arrow keys
  // can preview cards without committing the selection until Space/Enter.
  const [focusIndex, setFocusIndex] = useState<number>(() =>
    Math.max(
      0,
      candidates.findIndex((c) => c.subdir === selectedSubdir),
    ),
  );

  useEffect(() => {
    const idx = candidates.findIndex((c) => c.subdir === selectedSubdir);
    setFocusIndex(idx >= 0 ? idx : 0);
  }, [candidates, selectedSubdir]);

  if (candidates.length === 0) {
    return (
      <div className="space-y-2">
        <div className="rounded-md border border-dashed border-border bg-muted/30 px-3 py-3 text-xs text-muted-foreground">
          We couldn't find a plugin manifest at the repository root or a
          marketplace. Point us at the folder that holds the plugin and
          we'll try installing it directly.
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-foreground" htmlFor="picker-manual-subdir">
            Subdirectory
          </label>
          <Input
            id="picker-manual-subdir"
            placeholder="e.g. plugins/my-plugin"
            value={manualSubdir}
            onChange={(e: { target: { value: string } }) =>
              onManualSubdirChange(e.target.value)
            }
          />
          <p className="text-[11px] text-muted-foreground">
            Leave empty to install from the repo root.
          </p>
        </div>
      </div>
    );
  }

  const soloMode = candidates.length === 1;

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (soloMode) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      const next = Math.min(focusIndex + 1, candidates.length - 1);
      setFocusIndex(next);
      cardRefs.current[next]?.focus();
      onSelect(candidates[next]);
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const prev = Math.max(focusIndex - 1, 0);
      setFocusIndex(prev);
      cardRefs.current[prev]?.focus();
      onSelect(candidates[prev]);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setFocusIndex(0);
      cardRefs.current[0]?.focus();
      onSelect(candidates[0]);
    } else if (e.key === 'End') {
      e.preventDefault();
      const last = candidates.length - 1;
      setFocusIndex(last);
      cardRefs.current[last]?.focus();
      onSelect(candidates[last]);
    }
  }

  return (
    <div
      role="radiogroup"
      aria-label="Plugin to install"
      onKeyDown={handleKeyDown}
      className="space-y-2"
    >
      {candidates.map((candidate, i) => {
        const isSelected = candidate.subdir === selectedSubdir;
        return (
          <PluginCard
            key={candidate.subdir + '|' + candidate.name}
            ref={(el) => {
              cardRefs.current[i] = el;
            }}
            candidate={candidate}
            selected={isSelected}
            onSelect={(c) => {
              setFocusIndex(i);
              onSelect(c);
            }}
            soloMode={soloMode}
            // Only one tab stop in the radiogroup: the selected card, or the
            // first card if nothing is selected yet.
            tabIndex={isSelected || (!selectedSubdir && i === 0) ? 0 : -1}
          />
        );
      })}
    </div>
  );
}
